import React, {useState, useEffect} from 'react'
import axios from "axios";
import { toast } from '../utils/toast';
import { useSubmitGuard } from '../utils/useSubmitGuard';
import { errorMessage } from '../utils/errorMessage';
import { useNavigate , useLocation, useParams } from 'react-router-dom';
import { API_DETAILS } from '../App';

/*
 * Module scope, not inside VerifyEmail: as a nested component it was a new
 * component type on every render, so the code input was remounted and lost
 * focus on each keystroke.
 */
const Card = ({ children }) => (
   <div style={{
     minHeight: '100vh', backgroundColor: 'var(--youro-sand)',
     display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
   }}>
     <div style={{
       width: '100%', maxWidth: '440px',
       backgroundColor: 'var(--card)', border: '1px solid var(--youro-border)',
       borderRadius: 'var(--radius, 10px)', padding: '40px 32px', textAlign: 'center',
     }}>
       {children}
     </div>
   </div>
);

const VerifyEmail= (props) =>
{
    // how to get email address of the user and pass it on?
    // origins of /verify-password
    // const { state } = props.location;
    // const userEmail = state ? state.userEmail : null;
    //let userEmail = props.userEmail;

    // const location = useLocation();
    // const { userEmail, userId } = location.state || {};
    const [login, setLogin] = useState(0)

    const location = useLocation();
    const params = useParams();
    // Callers navigate with router state, which keeps the address out of the URL.
    // Fall back to the optional :userEmail path param so older links still work.
    const userEmail = location.state?.userEmail || params.userEmail;

    const [verify, setVerify] = useState(0);
    // The code lives in React state rather than being read off the DOM with
    // getElementById, so the button can react to it — disabled until six digits
    // are present, and blocked while a request is in flight.
    const [code, setCode] = useState('');
    const [sending, setSending] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // `sending` and `submitting` drive the buttons; the guard is the lock, since
    // state alone still let two clicks in the same frame through.
    const [, guard] = useSubmitGuard();
    const [resendIn, setResendIn] = useState(0);
    const navigate = useNavigate();

    // Codes last ten minutes, so a resend button that can be hammered is both
    // useless and a way to spam somebody's inbox.
    useEffect(() => {
        if (resendIn <= 0) return;
        const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendIn]);

    // The "Go to sign in" button used to call navigate() from inside the JSX,
    // so the redirect happened during render.
    useEffect(() => {
        if (login === 1) navigate("/login");
    }, [login, navigate]);

    const emailVerify = guard(async () => {

        // purpose scopes the code to email verification, so one issued here cannot
        // be replayed against the password-reset endpoint.
        const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/auth/send-otp/${userEmail}?purpose=VERIFY_EMAIL`;
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Content-Type': 'application/json'
            }
        };
        if (sending) return;
        setSending(true);
        try {
            await axios.get(url, config);
            // The response no longer carries the code — it is checked on the
            // server. Holding it here was what let anyone read someone else's.
            toast.success("We've emailed you a verification code. Enter it below to verify your account.");
            setCode('');
            setResendIn(30);
            setVerify(1);
        } catch (error) {
            // error.response is undefined when the request never reached the
            // server, so reading .data on it would throw inside the handler.
            const message =
                errorMessage(error, "We couldn't send a verification code. Wait a moment, then try again.");
            toast.error(message);
        } finally {
            setSending(false);
        }
    });

    const codeVerify = guard(async (data) => {
        // compare otp & entered value

        const url = API_DETAILS.baseUrl+ API_DETAILS.PORT + API_DETAILS.baseExtension +`/auth/verifyUser/${userEmail}`;
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Content-Type': 'application/json'
            }
        };

        if (submitting || code.length !== 6) return;

        // The code is checked by the server, which holds its hash and an expiry.
        // Comparing here was decorative: the value being compared against had
        // been handed to the browser, and the endpoint accepted the request
        // without a code at all.
        setSubmitting(true);
        try {
            await axios.put(url, { otp: code }, config);
            toast.success("Your email is verified. Sign in to get started.");
            setVerify(2);
        } catch (error) {
            const message =
                errorMessage(error, "We couldn't verify that code. Check it and try again.");
            toast.error(message);
            // A rejected code is almost always mistyped, so clear it rather than
            // making them select and delete six characters.
            setCode('');
        } finally {
            setSubmitting(false);
        }
    });

    // Accepts only digits and caps at six, so a pasted code with spaces or a
    // stray letter still lands correctly.
    const onCodeChange = (value) => setCode(value.replace(/\D/g, '').slice(0, 6));

    // Reached without an email — usually a direct visit or a refresh, which drops
    // the router state. Without this we'd request an OTP for "undefined".
    if (!userEmail) {
        return (
            <div className="Signupfamilymember-container">
                <div className="Login-Form-Container" style={{margin: '5% 30%'}}>
                    <div style={{textAlign: "center"}}>
                        <h1>Youro</h1>
                        <h2>We couldn't tell which account to verify</h2>
                        <p>This link is missing your email address. Sign in again and we'll send you a new verification code.</p>
                        <p className="color-secondary" style={{textDecoration: 'underline', cursor: 'pointer', fontWeight:'bold'}} onClick={() => navigate("/login")}>Back to sign in</p>
                    </div>
                </div>
            </div>
        );
    }

   // Shared shell so the three steps sit in the same card rather than three
   // differently-sized boxes, and so spacing comes from the design tokens.

   const primaryButton = (enabled) => ({
     width: '100%', marginTop: '24px', padding: '12px 24px',
     backgroundColor: enabled ? 'var(--youro-coral)' : 'var(--youro-stone)',
     color: '#ffffff', border: 'none', borderRadius: '8px',
     fontSize: '0.9375rem', fontWeight: 600,
     cursor: enabled ? 'pointer' : 'not-allowed',
   });

   return (
       <>

        {verify === 0 && (
          <Card>
            <h2 style={{ color: 'var(--youro-sage)', marginTop: 0 }}>Confirm your email</h2>
            <p style={{ color: 'var(--youro-charcoal)' }}>
              We'll send a six-digit code to
            </p>
            <p style={{ fontWeight: 600, wordBreak: 'break-all', margin: '4px 0 0' }}>{userEmail}</p>
            <button type="button" onClick={emailVerify} disabled={sending} style={primaryButton(!sending)}>
              {sending ? 'Sending…' : 'Send my code'}
            </button>
          </Card>
        )}

        {verify === 1 && (
          <Card>
            <h2 style={{ color: 'var(--youro-sage)', marginTop: 0 }}>Enter your code</h2>
            <p style={{ color: 'var(--youro-charcoal)', margin: 0 }}>
              We sent a six-digit code to
            </p>
            <p style={{ fontWeight: 600, wordBreak: 'break-all', margin: '4px 0 24px' }}>{userEmail}</p>

            <input
              type="text"
              id="otpInput"
              value={code}
              onChange={(e) => onCodeChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') codeVerify(); }}
              // Brings up the number pad on a phone and lets the browser fill the
              // code straight from the email.
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              placeholder="000000"
              aria-label="Six-digit verification code"
              style={{
                width: '100%', boxSizing: 'border-box',
                textAlign: 'center', fontSize: '2rem', fontWeight: 700,
                letterSpacing: '0.5em', textIndent: '0.5em',
                padding: '12px', borderRadius: '10px',
                border: '1px solid var(--youro-border)',
                backgroundColor: 'var(--card)', color: 'var(--youro-charcoal)', }}
            />

            <button
              type="button"
              onClick={codeVerify}
              disabled={submitting || code.length !== 6}
              style={primaryButton(!submitting && code.length === 6)}
            >
              {submitting ? 'Verifying…' : 'Verify'}
            </button>

            <p style={{ marginTop: '20px', fontSize: '0.8125rem', color: 'var(--youro-muted)' }}>
              The code expires in 10 minutes.{' '}
              {resendIn > 0 ? (
                <span>You can request another in {resendIn}s.</span>
              ) : (
                <span
                  onClick={emailVerify}
                  style={{ color: 'var(--youro-sky)', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Send a new code
                </span>
              )}
            </p>
          </Card>
        )}

        {verify === 2 && (
          <Card>
            <h2 style={{ color: 'var(--youro-sage)', marginTop: 0 }}>You're all set</h2>
            <p style={{ color: 'var(--youro-charcoal)' }}>
              Your email is confirmed. Sign in to get started with Youro.
            </p>
            <button type="button" onClick={() => setLogin(1)} style={primaryButton(true)}>
              Go to sign in
            </button>
          </Card>
        )}

      </>
    )
}

export default VerifyEmail;